<template>
  <div>
    <menu-content-container :padding-left-right="8" :margin-top="16">
      <template v-if="isDynamic">
        <content-row>
          <TDropdown
            v-if="mergetagChildOptions.length"
            class="w-full"
            data-testid="image-alt-mt-dropdown"
            :label="$t('forms.image_alt')"
            :data="mergetagChildOptions"
            :selected="altSelectedLabel"
            @selection-changed="onAltMergetagSelected($event)"
          ></TDropdown>
          <TInput v-else v-model="alt" :label="$t('forms.image_alt')"></TInput>
        </content-row>
        <content-row>
          <TDropdown
            v-if="mergetagChildOptions.length"
            class="w-full"
            data-testid="image-title-mt-dropdown"
            :label="$t('forms.image_title')"
            :data="mergetagChildOptions"
            :selected="titleSelectedLabel"
            @selection-changed="onTitleMergetagSelected($event)"
          ></TDropdown>
          <TInput
            v-else
            v-model="title"
            :label="$t('forms.image_title')"
          ></TInput>
        </content-row>
      </template>
    </menu-content-container>

    <menu-content-container
      :header="$t('product.img_size')"
      :padding-left-right="8"
    >
      <content-row :margin-top="16">
        <t-slider
          v-model="width"
          data-testid="product-image-width"
          xs
          :min="30"
          :max="maxImageWidth"
          :unit="'px'"
        ></t-slider>
      </content-row>
    </menu-content-container>

    <menu-content-container
      :header="$t('forms.image_align')"
      :padding-left-right="8"
    >
      <content-row justify-between>
        <TButtonGroup>
          <TGroupButton
            data="left"
            data-testid="align-left"
            small
            :active="align === 'left'"
            @button-clicked="setAlign('left')"
          >
            <AligmentLeft />
          </TGroupButton>
          <TGroupButton
            data="center"
            data-testid="align-center"
            small
            :active="align === 'center'"
            @button-clicked="setAlign('center')"
          >
            <AligmentCenter />
          </TGroupButton>
          <TGroupButton
            data="right"
            data-testid="align-right"
            small
            :active="align === 'right'"
            @button-clicked="setAlign('right')"
          >
            <AligmentRight />
          </TGroupButton>
        </TButtonGroup>

        <t-switch
          v-model="fluidOnMobile"
          :label="$t('forms.fluid_on_mobile')"
        ></t-switch>
      </content-row>
    </menu-content-container>

    <borderRadiusForm
      data-testid="image-border-radius"
      :product="true"
      :prodstructure="prodstructure"
      :padding-left-right="8"
      :label="$t('forms.border_radius')"
      :min-y="0"
      :max-y="width"
      :min-x="0"
      :max-x="width"
      prefix="image-"
    ></borderRadiusForm>

    <paddingForm
      :product="true"
      :prodstructure="prodstructure"
      :padding-left-right="8"
      :border-bottom="false"
      class="!mb-2"
      prefix="image-"
    ></paddingForm>
  </div>
</template>

<script lang="ts" setup>
  import paddingForm from "../paddingForm.vue";
  import borderRadiusForm from "../borderRadiusForm.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useApiBlocksStore } from "@/stores/apiBlocks";
  import { useMergeTagsStore } from "@/stores/mergeTags";

  import { AligmentLeft, AligmentCenter, AligmentRight } from "@topol/icons";
  import {
    TButtonGroup,
    TDropdown,
    TGroupButton,
    TInput,
    TSliderClassic as TSlider,
    TSwitch,
  } from "@topol/twig-ui";
  import type { TDropdownOption } from "@topol/twig-ui";
  import { useI18n } from "vue-i18n";

  import { computed, watch } from "vue";

  const { t } = useI18n();

  const mjmlStore = useMjmlStore();
  const apiBlocksStore = useApiBlocksStore();
  const mergeTagsStore = useMergeTagsStore();

  const prodstructure = computed(() => {
    const prodstructure = mjmlStore.getActiveBlockAttrString("prodstructure");

    if (prodstructure) {
      return prodstructure;
    }

    return false;
  });

  const activeBlock = computed(() => mjmlStore.getActiveBlock as any);

  const isDynamic = computed(() => activeBlock.value?.mode === "dynamic");

  function updateTemplateItemField(field: string, value: string) {
    const block = activeBlock.value;
    if (!block) return;
    const items = Array.isArray(block.items) ? [...block.items] : [];
    const template = items[0] ? { ...items[0] } : {};
    template[field] = value;
    items[0] = template;
    mjmlStore.updateBlockProp({
      prop: "items",
      value: items,
      product: true,
      prodstructure: prodstructure.value,
    });
  }

  const alt = computed({
    get() {
      return activeBlock.value?.items?.[0]?.["image-alt"] || "";
    },
    set(newValue: string) {
      updateTemplateItemField("image-alt", newValue);
    },
  });

  const title = computed({
    get() {
      return activeBlock.value?.items?.[0]?.["image-title"] || "";
    },
    set(newValue: string) {
      updateTemplateItemField("image-title", newValue);
    },
  });

  const blockProperties = computed(() => {
    const block = activeBlock.value;
    if (!block?.blockName) return null;
    return apiBlocksStore.blocks.get(block.blockName) ?? null;
  });

  const EXCLUDED_KEYS = new Set(["layout", "image", "href", "button"]);

  const API_STRUCTURE_KEY_OVERRIDES: Record<string, string> = {
    "price-before": "price_before",
    price: "price_vat",
  };

  const mergetagChildOptions = computed<TDropdownOption[]>(() => {
    const props = blockProperties.value;
    const structure = props?.blockStructure;
    const apiStructure = props?.apiStructure;
    const { start, end } = mergeTagsStore.MTSyntax;
    const noneOption: TDropdownOption = {
      id: "__none__",
      label: "––",
      value: "",
    };
    if (!structure) return [noneOption];
    const fieldOptions = Object.keys(structure)
      .filter((key) => !EXCLUDED_KEYS.has(key))
      .map((key) => {
        const entry = structure[key] || {};
        const apiKey = API_STRUCTURE_KEY_OVERRIDES[key] ?? key;
        const inner = apiStructure?.[apiKey] || key;
        return {
          id: key,
          label: entry.label || key,
          value: `${start}${inner}${end}`,
        };
      });
    return [noneOption, ...fieldOptions];
  });

  const altSelectedLabel = computed(() => {
    const match = mergetagChildOptions.value.find((o) => o.value === alt.value);
    return match?.label || alt.value || t("forms.image_alt");
  });

  const titleSelectedLabel = computed(() => {
    const match = mergetagChildOptions.value.find(
      (o) => o.value === title.value
    );
    return match?.label || title.value || t("forms.image_title");
  });

  function onAltMergetagSelected(option: TDropdownOption) {
    alt.value = option.value as string;
  }

  function onTitleMergetagSelected(option: TDropdownOption) {
    title.value = option.value as string;
  }

  const fluidOnMobile = computed({
    get() {
      return (
        mjmlStore.getActiveBlockAttrString("image-fluid-on-mobile") === "true"
      );
    },

    set(value: boolean) {
      mjmlStore.updateBlockAttribute({
        prop: "image-fluid-on-mobile",
        value: value ? "true" : "",
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  const align = computed({
    get() {
      const align = mjmlStore.getActiveBlockAttrString("image-align");
      if (align) {
        return align;
      } else {
        return "center";
      }
    },
    set(val: string) {
      mjmlStore.updateBlockAttribute({
        prop: "image-align",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  const DEFAULT_CONTAINER_WIDTH = 600;

  const containerWidth = computed(() => {
    const column = mjmlStore.getSelectedColumn;

    const bodyWidth = mjmlStore.getBodyWidth || DEFAULT_CONTAINER_WIDTH;
    const colPercent = parseInt(column?.attributes?.width ?? "") || 100;

    return Math.round((bodyWidth * colPercent) / 100);
  });

  const maxImageWidth = computed(() => {
    const block = activeBlock.value;
    const layout = block?.layout;
    if (layout === "image-text" || layout === "text-image") {
      return Math.floor(containerWidth.value / 2);
    }

    const perRow = Math.max(
      1,
      Math.min(4, Math.min(block?.maxPerRow || 1, block.items?.length || 1))
    );
    return Math.floor(containerWidth.value / perRow);
  });

  const width = computed({
    get() {
      const current = mjmlStore.getActiveBlockAttrInt("image-width");
      if (!current) return maxImageWidth.value;
      return Math.min(current, maxImageWidth.value);
    },
    set(val: number) {
      const clamped = Math.max(30, Math.min(val, maxImageWidth.value));
      mjmlStore.updateBlockAttribute({
        prop: "image-width",
        value: clamped,
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  watch(
    maxImageWidth,
    (newMax) => {
      const current = mjmlStore.getActiveBlockAttrInt("image-width");
      if (current && current > newMax) {
        width.value = newMax;
      }
    },
    { immediate: true }
  );

  function setAlign(direction: string) {
    align.value = direction;
  }
</script>
